import * as pc from "playcanvas";

import type { Number3 } from "../types";
import { isApproximately, normalizeAngle } from "./Number";

export interface ReadonlyVector3 {
  get x(): number;
  get y(): number;
  get z(): number;

  clone(): Vector3;
  copyToArray(array: Array<number> | Float32Array | Float64Array, offset?: number): this;
  distance(rhs: ReadonlyVector3): number;
  distanceSq(rhs: ReadonlyVector3): number;
  dot(rhs: ReadonlyVector3): number;
  equals(rhs: ReadonlyVector3): boolean;
  equalsApprox(rhs: ReadonlyVector3, epsilon?: number): boolean;
  length(): number;
  lengthSq(): number;
  toString(): string;
  toArray(): Number3;

  isApproximately(rhs: ReadonlyVector3): boolean;
  isFinite(): boolean;
  isNaN(): boolean;

  // Convert for use in external JS code.
  // This is unsafe and should only be used when necessary.
  unsafeCast(): Vector3;
}

declare module "playcanvas" {
  interface Vec3 extends ReadonlyVector3 {
    x: number;
    y: number;
    z: number;

    add(rhs: ReadonlyVector3): this;
    add2(lhs: ReadonlyVector3, rhs: ReadonlyVector3): this;
    addScalar(scalar: number): this;
    addScaled(rhs: ReadonlyVector3, scalar: number): this;
    ceil(src?: ReadonlyVector3): this;
    copy(rhs: ReadonlyVector3): this;
    copyToArray(array: Array<number> | Float32Array | Float64Array, offset?: number): this;
    cross(lhs: ReadonlyVector3, rhs: ReadonlyVector3): this;
    distance(rhs: ReadonlyVector3): number;
    distanceSq(rhs: ReadonlyVector3): number;
    div(rhs: ReadonlyVector3): this;
    div2(lhs: ReadonlyVector3, rhs: ReadonlyVector3): this;
    divScalar(scalar: number): this;
    dot(rhs: ReadonlyVector3): number;
    equals(rhs: ReadonlyVector3): boolean;
    equalsApprox(rhs: ReadonlyVector3, epsilon?: number): boolean;
    floor(src?: ReadonlyVector3): this;
    lerp(lhs: ReadonlyVector3, rhs: ReadonlyVector3, alpha: number): this;
    max(rhs: ReadonlyVector3): this;
    min(rhs: ReadonlyVector3): this;
    mul(rhs: ReadonlyVector3): this;
    mul2(lhs: ReadonlyVector3, rhs: ReadonlyVector3): this;
    mulScalar(scalar: number): this;
    normalize(src?: ReadonlyVector3): this;
    optimizeEulerFavorY(): this;
    project(rhs: ReadonlyVector3): this;
    round(src?: ReadonlyVector3): this;
    set(x: number, y: number, z: number): this;
    sub(rhs: ReadonlyVector3): this;
    sub2(lhs: ReadonlyVector3, rhs: ReadonlyVector3): this;
    subScalar(scalar: number): this;
    toArray(): Number3;

    recycle(): void;
  }
}

pc.Vec3.prototype.isFinite = function () {
  return Number.isFinite(this.x) && Number.isFinite(this.y) && Number.isFinite(this.z);
};

pc.Vec3.prototype.isNaN = function () {
  return isNaN(this.x) || isNaN(this.y) || isNaN(this.z);
};

pc.Vec3.prototype.isApproximately = function (rhs: ReadonlyVector3) {
  return isApproximately(this.x, rhs.x) && isApproximately(this.y, rhs.y) && isApproximately(this.z, rhs.z);
};

pc.Vec3.prototype.recycle = function () {
  pool.push(this);
};

pc.Vec3.prototype.unsafeCast = function () {
  return this;
};

pc.Vec3.prototype.optimizeEulerFavorY = function () {
  const rx = normalizeAngle(this.x);
  const ry = normalizeAngle(this.y);
  const rz = normalizeAngle(this.z);

  if (Math.abs(rx) + Math.abs(rz) > Math.abs(ry)) {
    // try to fold rotations into Y by adding 180 modulo
    const newRy = normalizeAngle(180 - ry);
    const newRx = normalizeAngle(180 + rx);
    const newRz = normalizeAngle(180 + rz);

    // if this reduces X/Z sum, keep it
    if (Math.abs(newRx) + Math.abs(newRz) < Math.abs(rx) + Math.abs(rz)) {
      return this.set(newRx, newRy, newRz);
    }
  }
  return this.set(rx, ry, rz);
};

const pool: Vector3[] = [];

// @ts-expect-error - Override static constants with ReadonlyVector3 type (intentionally incompatible with base class)
export class Vector3 extends pc.Vec3 {
  static override ZERO = Vector3.makeReadonly(0, 0, 0);
  static override HALF = Vector3.makeReadonly(0.5, 0.5, 0.5);
  static override ONE = Vector3.makeReadonly(1, 1, 1);

  static override UP = Vector3.makeReadonly(0, 1, 0);
  static override DOWN = Vector3.makeReadonly(0, -1, 0);
  static override RIGHT = Vector3.makeReadonly(1, 0, 0);
  static override LEFT = Vector3.makeReadonly(-1, 0, 0);
  // Align with PC's forward direction (which is opposite of our forward direction)
  static override FORWARD = Vector3.makeReadonly(0, 0, -1);
  static override BACK = Vector3.makeReadonly(0, 0, 1);

  static isInstance(value: unknown): value is Vector3 {
    return value instanceof pc.Vec3;
  }

  static fromArray(array: Readonly<Number3>) {
    return this.make(array[0], array[1], array[2]);
  }

  /**
   * @summary Creates a vector from a variety of input types.
   */
  static from(json: unknown) {
    if (Vector3.isInstance(json)) {
      return json.clone();
    } else if (typeof json === "number") {
      return this.make(json, json, json);
    } else if (
      Array.isArray(json) &&
      json.length >= 3 &&
      typeof json[0] === "number" &&
      typeof json[1] === "number" &&
      typeof json[2] === "number"
    ) {
      return this.make(json[0], json[1], json[2]);
    } else if (
      typeof json === "object" &&
      json !== null &&
      "x" in json &&
      "y" in json &&
      "z" in json &&
      typeof json.x === "number" &&
      typeof json.y === "number" &&
      typeof json.z === "number"
    ) {
      return this.make(json.x, json.y, json.z);
    }

    throw new Error("Invalid input for Vector3.from");
  }

  /**
   * @summary Combines two vectors with a blend factor.
   */
  static lerp(lhs: ReadonlyVector3, rhs: ReadonlyVector3, blend: number) {
    return Vector3.make(
      lhs.x + blend * (rhs.x - lhs.x),
      lhs.y + blend * (rhs.y - lhs.y),
      lhs.z + blend * (rhs.z - lhs.z)
    );
  }

  /**
   * @summary Creates a new vector with the given components.
   */
  static make(x = 0, y = 0, z = 0): Vector3 {
    return pool.pop()?.set(x, y, z) ?? new Vector3(x, y, z);
  }

  /**
   * Create a readonly Vector that cannot be recycled.
   */
  static makeReadonly(x: number, y: number, z: number): ReadonlyVector3 {
    const vector = new Vector3(x, y, z);
    Object.freeze(vector);
    return vector;
  }

  /**
   * @summary Creates a new uninitialized vector.
   */
  static makeUninitialized() {
    return pool.pop() ?? new Vector3();
  }
}
